import * as React from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  BadgeCheck,
  Bot,
  Boxes,
  Cpu,
  CreditCard,
  FileSignature,
  Home as HomeIcon,
  LogOut,
  Menu,
  Send,
  Sparkles,
  Store,
  User,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { signOut } from '@/lib/auth';
import { useSession } from '@/contexts/SessionContext';
import { accountMetricsKey, fetchAccountMetrics } from '@/lib/queries';
import { cn } from '@/lib/utils';

type Icon = typeof HomeIcon;

interface NavItem {
  to: string;
  label: string;
  icon: Icon;
  end?: boolean;
}

// Fixed nav (always present). The dynamic "Your workspaces" section sits
// between `Connected agents` and `Catalog`.
const TOP_ITEMS: NavItem[] = [
  { to: '/dashboard', label: 'Home', icon: HomeIcon, end: true },
  { to: '/dashboard/connections', label: 'Connected agents', icon: Bot },
];
const BOTTOM_ITEMS: NavItem[] = [
  { to: '/dashboard/catalog', label: 'Catalog', icon: Store },
  { to: '/dashboard/billing', label: 'Billing', icon: CreditCard },
  { to: '/dashboard/account', label: 'Account', icon: User },
];

// The workspace registry — a product family maps to one workspace entry. Batons
// is pinned first (the primary category); the rest appear only with usage, in
// usage order (Brief 185 / the locked IA: "your usage is the navigation").
interface Workspace {
  key: string;
  label: string;
  to: string;
  icon: Icon;
  products: string[];
}
const WORKSPACES: Workspace[] = [
  { key: 'baton', label: 'Batons', to: '/dashboard/batons', icon: Boxes, products: ['baton'] },
  { key: 'esign', label: 'E-sign', to: '/dashboard/esign', icon: FileSignature, products: ['esigndoc', 'esign'] },
  { key: 'idverify', label: 'ID verification', to: '/dashboard/idverify', icon: BadgeCheck, products: ['idverify'] },
  { key: 'llm', label: 'LLM tasks', to: '/dashboard/llm', icon: Sparkles, products: ['llm'] },
  { key: 'cputools', label: 'Compute & convert', to: '/dashboard/cputools', icon: Cpu, products: ['cputools', 'pdf', 'media', 'doc', 'image', 'csv', 'data'] },
  { key: 'courier', label: 'Courier', to: '/dashboard/courier', icon: Send, products: ['bridge', 'courier'] },
];

/** Derive the workspace nav from usage: Batons first, then others that have
 *  usage, ordered by the backend's most-recent-first usedProducts list. */
function deriveWorkspaces(used: string[]): Workspace[] {
  const baton = WORKSPACES.find((w) => w.key === 'baton')!;
  const rest: Workspace[] = [];
  const seen = new Set<string>(['baton']);
  for (const product of used) {
    const ws = WORKSPACES.find((w) => w.key !== 'baton' && w.products.includes(product));
    if (ws && !seen.has(ws.key)) {
      seen.add(ws.key);
      rest.push(ws);
    }
  }
  return [baton, ...rest];
}

function navLinkClass({ isActive }: { isActive: boolean }): string {
  // Brief 117 idiom — no filled pill; active reads via weight + foreground.
  return cn(
    'flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors',
    isActive ? 'font-medium text-foreground' : 'text-muted-foreground hover:text-foreground',
  );
}

function SidebarNav({ onNavigate }: { onNavigate?: () => void }) {
  const metricsQ = useQuery({ queryKey: accountMetricsKey, queryFn: fetchAccountMetrics });
  const used = (metricsQ.data?.usedProducts ?? []).map((p) => p.product);
  const workspaces = deriveWorkspaces(used);

  return (
    <nav className="flex flex-col gap-1">
      {TOP_ITEMS.map(({ to, label, icon: Icon, end }) => (
        <NavLink key={to} to={to} end={end} onClick={onNavigate} className={navLinkClass}>
          <Icon className="h-4 w-4" />
          {label}
        </NavLink>
      ))}

      <p className="px-3 pb-1 pt-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Your workspaces
      </p>
      {workspaces.map(({ to, label, icon: Icon }) => (
        <NavLink key={to} to={to} onClick={onNavigate} className={navLinkClass}>
          <Icon className="h-4 w-4" />
          {label}
        </NavLink>
      ))}

      <div className="my-2 border-t" />
      {BOTTOM_ITEMS.map(({ to, label, icon: Icon }) => (
        <NavLink key={to} to={to} onClick={onNavigate} className={navLinkClass}>
          <Icon className="h-4 w-4" />
          {label}
        </NavLink>
      ))}
    </nav>
  );
}

function UserMenu() {
  const { session, clear } = useSession();
  const navigate = useNavigate();

  const onSignOut = async () => {
    clear();
    await signOut();
    navigate('/signin', { replace: true });
  };

  const label =
    session?.authType === 'oauth'
      ? session.email
      : session?.authType === 'wallet'
        ? `${session.walletAddress.slice(0, 6)}…${session.walletAddress.slice(-4)}`
        : 'Signed in';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-2">
          <User className="h-4 w-4" />
          <span className="hidden sm:inline">{label}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>{label}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate('/dashboard/account')}>
          <User className="h-4 w-4" />
          Account
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={onSignOut}>
          <LogOut className="h-4 w-4" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function DashboardLayout() {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="sticky top-0 z-40 border-b bg-background/80 backdrop-blur">
        <div className="container flex h-16 items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="md:hidden"
                  aria-label="Open navigation"
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72">
                <SheetHeader>
                  <SheetTitle>Relaystation</SheetTitle>
                </SheetHeader>
                <div className="mt-6">
                  <SidebarNav onNavigate={() => setMobileOpen(false)} />
                </div>
              </SheetContent>
            </Sheet>
            <Link
              to="/dashboard"
              className="flex items-center gap-2 text-base font-semibold tracking-tight"
            >
              <img src="/logo.svg" alt="Relaystation logo" className="h-7 w-auto" />
              Relaystation
            </Link>
          </div>
          <UserMenu />
        </div>
      </header>

      <div className="container flex flex-1 gap-8 py-8">
        {/* Desktop sidebar. Mobile users open the Sheet from the header. */}
        <aside className="hidden w-56 shrink-0 md:block">
          <div className="sticky top-24">
            <SidebarNav />
          </div>
        </aside>
        <main className="min-w-0 flex-1">
          <Outlet />
        </main>
      </div>

      <footer className="border-t bg-background">
        <div className="container flex h-12 items-center justify-between text-xs text-muted-foreground">
          <span>relaystation · v0.1</span>
          <a
            href="https://status.relaystation.ai"
            className="hover:text-foreground"
            target="_blank"
            rel="noreferrer"
          >
            Status
          </a>
        </div>
      </footer>
    </div>
  );
}
